import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../utils/axiosConfig";
import Select, { components as selectComponents } from "react-select";
import BarraTrafico from "../Modulos/BarraTrafico";
import Tooltip from "../components/Tooltip";
import PreloaderHistorico from "../components/preloaders/preloader-historico";
import ModalBaseSm from "../components/modals/modal-base-sm";
import { formatElapsedReportTime, formattedGlobalDate } from "../utils/formattedDates";
import { Toaster, toast } from "react-hot-toast";
import Swal from "sweetalert2";
import checkPerm from "../utils/checkPerm";
import { registerOpenStreetMap, setOpenStreetMapView } from "../utils/openStreetMapType";
import "./Css/traficoPorRecoger.css";
import "./Css/traficoResponsive.css";

const DEFAULT_CENTER = { lat: 10.992716649650994, lng: -74.80259436365039 };

const filterSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 20000 }),
  menu: (base) => ({ ...base, zIndex: 20000 }),
};

const getDelayBadgeStyle = (option) => {
  const delayClass = option.delayClass || (["yellow", "orange", "red"].includes(option.value) ? option.value : null);
  if (delayClass === "normal") return { backgroundColor: "#fff", color: "#71839b" };
  if (delayClass === "red") return { backgroundColor: "#f39a97", color: "#6f1f1b" };
  if (delayClass === "orange") return { backgroundColor: "rgba(224, 160, 25, 0.24)", color: "#8a5b00" };
  if (delayClass === "yellow") return { backgroundColor: "#fff0b8", color: "#806500" };
  return undefined;
};

const formatFilterOptionLabel = (option) => (
  <span className="trafico-recoger-filter-option-label">
    <span>{option.label}</span>
    <span
      className={`trafico-recoger-filter-count-badge ${option.delayClass || ["yellow", "orange", "red"].includes(option.value) ? `is-delay-${option.delayClass || option.value}` : ""}`}
      style={getDelayBadgeStyle(option)}
    >
      {option.count}
    </span>
  </span>
);

const DelayOption = (props) => {
  const { data } = props;
  const delayClass = data.delayClass || data.value;

  return (
    <selectComponents.Option {...props}>
      <span className="trafico-recoger-filter-option-label">
        <span>{data.label}</span>
        <span
          className={`trafico-recoger-filter-count-badge is-delay-${delayClass}`}
          style={getDelayBadgeStyle(data)}
        >
          {data.count}
        </span>
      </span>
    </selectComponents.Option>
  );
};

const SORT_KEYS = {
  company: "company",
  agency: "agency",
  device: "device",
  driver: "driver",
  novelty: "novelty",
  lastReport: "lastReport",
  uninstallTime: "uninstallTime",
  location: "location",
};

const DELAY_RANGES = {
  normal: "normal",
  yellow: "yellow",
  orange: "orange",
  red: "red",
};

const DELAY_MARKER_COLORS = {
  normal: "#ffffff",
  yellow: "#fff0b8",
  orange: "#f3c28f",
  red: "#f39a97",
};

const detachMapMarker = (marker) => {
  if (marker?.setMap) marker.setMap(null);
  else if (marker) marker.map = null;
};

const getDelayCategory = (contract, currentTime) => {
  const elapsed = getElapsedSortValue(contract?.FechaHoraFin, currentTime);
  if (elapsed === null) return null;

  const elapsedDays = Math.floor(elapsed / 86400000);
  if (elapsedDays >= 9) return DELAY_RANGES.red;
  if (elapsedDays >= 4) return DELAY_RANGES.orange;
  if (elapsedDays >= 1) return DELAY_RANGES.yellow;
  return DELAY_RANGES.normal;
};

const getElapsedSortValue = (dateValue, currentTime) => {
  const timestamp = new Date(dateValue).getTime();
  return Number.isFinite(timestamp) ? Math.max(0, currentTime - timestamp) : null;
};

const getSortValue = (contract, key, currentTime) => {
  switch (key) {
    case SORT_KEYS.company:
      return contract?.NombreEmpresa;
    case SORT_KEYS.agency:
      return contract?.Agencia;
    case SORT_KEYS.device:
      return contract?.DeviceID;
    case SORT_KEYS.driver:
      return `${contract?.NombreConductor ?? ""} ${contract?.PlacaTruck ?? ""} ${contract?.MovilConductor ?? ""}`;
    case SORT_KEYS.novelty:
      return contract?.LastNovedadRecogida?.trim?.() || contract?.LastReportNota;
    case SORT_KEYS.lastReport:
      return getElapsedSortValue(contract?.LoksysServerTime, currentTime);
    case SORT_KEYS.uninstallTime:
      return getElapsedSortValue(contract?.FechaHoraFin, currentTime);
    case SORT_KEYS.location:
      return contract?.LastReportUbica;
    default:
      return "";
  }
};

const SortableHeader = ({ label, sortKey, sortConfig, onSort }) => {
  const isActive = sortConfig.key === sortKey;
  const direction = isActive ? sortConfig.direction : null;

  return (
    <th aria-sort={direction ? (direction === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className={`trafico-recoger-sort-button ${isActive ? "is-active" : ""}`}
        onClick={() => onSort(sortKey)}
        aria-label={`Ordenar ${label} ${direction === "asc" ? "de mayor a menor" : "de menor a mayor"}`}
      >
        <span>{label}</span>
        <i
          className={`bx ${direction === "asc" ? "bx-chevron-up" : direction === "desc" ? "bx-chevron-down" : "bx-chevron-down"}`}
          aria-hidden="true"
        />
      </button>
    </th>
  );
};

const getRows = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.recordset)) return payload.recordset;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return Array.isArray(payload) ? payload : [];
};

const getCoordinates = (contract) => {
  const lat = Number(contract?.LastLatitude ?? contract?.latitude ?? contract?.Latitud);
  const lng = Number(contract?.LastLongitude ?? contract?.longitude ?? contract?.Longitud);

  if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) return null;
  return { lat, lng };
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const getPlateKey = (contract) => String(contract?.PlacaTruck ?? "").trim().toUpperCase();

const getContractsWithSamePlate = (rows, contract) => {
  if (!contract) return [];
  const plateKey = getPlateKey(contract);
  if (!plateKey) return [contract];

  const relatedContracts = rows.filter((item) => getPlateKey(item) === plateKey);
  return relatedContracts.length > 0 ? relatedContracts : [contract];
};

const markRepeatedPlates = (rows) => {
  const plateCounts = rows.reduce((counts, contract) => {
    const plateKey = getPlateKey(contract);
    if (plateKey) counts.set(plateKey, (counts.get(plateKey) || 0) + 1);
    return counts;
  }, new Map());

  return rows.map((contract) => ({
    ...contract,
    isRefuerzoPorPlaca: Boolean(getPlateKey(contract) && plateCounts.get(getPlateKey(contract)) > 1),
  }));
};

const PlateLabel = ({ contract }) => (
  <>
    {displayValue(contract?.PlacaTruck)}
    {contract?.isRefuerzoPorPlaca && (
      <span className="badge badge-danger ml-1 trafico-recoger-refuerzo-badge" title="Refuerzo">
        R
      </span>
    )}
  </>
);

const getFilterValue = (contract, field) => {
  const value = String(contract?.[field] ?? "").trim();
  return value || "__SIN_VALOR__";
};

const getCountedFilterOptions = (items, field, emptyLabel) => {
  const counts = new Map();

  items.forEach((contract) => {
    const value = getFilterValue(contract, field);
    counts.set(value, (counts.get(value) || 0) + 1);
  });

  return Array.from(counts.entries())
    .sort(([first], [second]) => {
      const firstLabel = first === "__SIN_VALOR__" ? emptyLabel : first;
      const secondLabel = second === "__SIN_VALOR__" ? emptyLabel : second;
      return firstLabel.localeCompare(secondLabel, "es", { sensitivity: "base" });
    })
    .map(([value, count]) => ({
      value,
      label: value === "__SIN_VALOR__" ? emptyLabel : value,
      count,
    }));
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return displayValue(value);
  return date.toLocaleString("es-CO", { dateStyle: "short", timeStyle: "medium" });
};

const sortNovedades = (rows) => [...rows].sort((first, second) => {
  const firstDate = new Date(first?.DatetimSeg ?? first?.DatetimeSeg ?? first?.Fecha ?? 0).getTime();
  const secondDate = new Date(second?.DatetimSeg ?? second?.DatetimeSeg ?? second?.Fecha ?? 0).getTime();
  return firstDate - secondDate;
});

const getSatelliteLink = (contract) => {
  const coordinates = getCoordinates(contract);
  return coordinates
    ? `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`
    : "Sin coordenadas";
};

const getContractClipboardText = (contract) => [
  `Cliente: ${displayValue(contract?.NombreEmpresa)}`,
  `Dispositivo: ${displayValue(contract?.DeviceID)}`,
  `Placa: ${displayValue(contract?.PlacaTruck)}`,
  `Contenedor: ${displayValue(contract?.ContainerNum)}`,
  `NombreConductor: ${displayValue(contract?.NombreConductor)}`,
  `Movil Conductor: ${displayValue(contract?.MovilConductor)}`,
  `Link ubicación satelital: ${getSatelliteLink(contract)}`,
].join("\n");

const createMapInfoContent = (contract, novedades = [], isLoading = false, onSaveNovedad) => {
  const content = document.createElement("div");
  content.className = "trafico-recoger-map-info";

  const title = document.createElement("strong");
  title.textContent = displayValue(contract?.ContractID);
  content.appendChild(title);

  const company = document.createElement("span");
  company.textContent = displayValue(contract?.NombreEmpresa);
  content.appendChild(company);

  const device = document.createElement("span");
  device.textContent = `Dispositivo: ${displayValue(contract?.DeviceID)}`;
  content.appendChild(device);

  const plate = document.createElement("span");
  plate.textContent = `Placa: ${displayValue(contract?.PlacaTruck)}${contract?.isRefuerzoPorPlaca ? " · R Refuerzo" : ""}`;
  content.appendChild(plate);

  const novedadesTitle = document.createElement("strong");
  novedadesTitle.className = "trafico-recoger-map-info-heading";
  novedadesTitle.textContent = "Novedades";
  content.appendChild(novedadesTitle);
  let chatElement = null;

  if (isLoading) {
    const loading = document.createElement("span");
    loading.textContent = "Cargando novedades...";
    content.appendChild(loading);
  } else if (novedades.length === 0) {
    const empty = document.createElement("span");
    empty.textContent = "Sin novedades registradas";
    content.appendChild(empty);
  } else {
    chatElement = document.createElement("div");
    chatElement.className = "trafico-recoger-map-info-chat";

    novedades.forEach((novedad) => {
      const item = document.createElement("article");
      item.className = "trafico-recoger-map-info-message";

      const meta = document.createElement("div");
      meta.className = "trafico-recoger-map-info-message-meta";
      const user = document.createElement("strong");
      user.textContent = displayValue(novedad?.UserSeg);
      const date = document.createElement("time");
      const rawDate = novedad?.DatetimSeg ?? novedad?.DatetimeSeg ?? novedad?.Fecha;
      date.textContent = formattedGlobalDate(rawDate) || formatDate(rawDate);
      meta.append(user, date);

      const message = document.createElement("p");
      message.textContent = displayValue(novedad?.NotasSeg);
      item.append(meta, message);
      chatElement.appendChild(item);
    });

    content.appendChild(chatElement);
  }

  if (!isLoading && onSaveNovedad) {
    const form = document.createElement("form");
    form.className = "trafico-recoger-map-info-form";

    const textarea = document.createElement("textarea");
    textarea.rows = 2;
    textarea.maxLength = 1000;
    textarea.placeholder = "Escribe una novedad...";
    textarea.setAttribute("aria-label", "Nueva novedad");

    const actions = document.createElement("div");
    actions.className = "trafico-recoger-map-info-form-actions";
    const saveButton = document.createElement("button");
    saveButton.type = "submit";
    saveButton.textContent = "Guardar";
    actions.appendChild(saveButton);
    form.append(textarea, actions);

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const nota = textarea.value.trim();
      if (!nota) return;

      saveButton.disabled = true;
      saveButton.textContent = "Guardando...";

      try {
        const novedadesActualizadas = await onSaveNovedad(contract, nota);
        content.replaceWith(createMapInfoContent(contract, novedadesActualizadas, false, onSaveNovedad));
        toast.success("Novedad guardada correctamente.");
      } catch (requestError) {
        console.error("Error al guardar novedad desde el mapa:", requestError);
        saveButton.disabled = false;
        saveButton.textContent = "Guardar";
        toast.error(requestError.message || "No fue posible guardar la novedad.");
      }
    });

    content.appendChild(form);
  }

  if (chatElement) {
    window.requestAnimationFrame(() => {
      chatElement.scrollTop = chatElement.scrollHeight;
    });
  }

  return content;
};

const TraficoPorRecoger = () => {
  const [contracts, setContracts] = useState([]);
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [selectedDelayRanges, setSelectedDelayRanges] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [isContractListCollapsed, setIsContractListCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mapReady, setMapReady] = useState(false);
  const [selectedMapType, setSelectedMapType] = useState("roadmap");
  const [novedadesModalOpen, setNovedadesModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [novedades, setNovedades] = useState([]);
  const [novedadesLoading, setNovedadesLoading] = useState(false);
  const [novedadesError, setNovedadesError] = useState("");
  const [notasSeg, setNotasSeg] = useState("");
  const [savingNovedad, setSavingNovedad] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const pageContentRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const mapRetryRef = useRef(null);
  const novedadesListRef = useRef(null);
  const infoNovedadesRequestRef = useRef(0);

  const toggleNavigationMenu = () => {
    document.querySelector('[class*="sidenav-toggle"]')?.click();
  };

  const toggleMapFullscreen = async () => {
    const fullscreenElement = pageContentRef.current;
    if (!fullscreenElement) return;

    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
        return;
      }

      if (fullscreenElement.requestFullscreen) {
        await fullscreenElement.requestFullscreen();
      } else if (fullscreenElement.webkitRequestFullscreen) {
        fullscreenElement.webkitRequestFullscreen();
      } else {
        setIsMapFullscreen((fullscreen) => !fullscreen);
      }
    } catch (fullscreenError) {
      console.error("No fue posible cambiar el modo pantalla completa:", fullscreenError);
      toast.error("No fue posible activar la pantalla completa.");
    }
  };

  const handleMapTypeChange = (mapType) => {
    setSelectedMapType(mapType);
    setOpenStreetMapView(mapRef.current, mapType);
  };

  const copyContractData = async (contract) => {
    const text = getContractClipboardText(contract);

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }

      toast.success("Datos copiados en el portapapeles.");
    } catch (copyError) {
      console.error("Error al copiar datos del contrato:", copyError);
      toast.error("No fue posible copiar los datos.");
    }
  };

  const requestNovedades = async (contract) => {
    const response = await axios.post(
      `${process.env.REACT_APP_SERVER}/operaciones/getnovedadesporrecoger`,
      JSON.stringify({ contrato: contract.ContractID }),
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("tkn")}`,
        },
        withCredentials: true,
      }
    );

    console.log("[TRAFICO_POR_RECOGER][GET_NOVEDADES]", {
      contrato: contract.ContractID,
      respuesta: response.data,
    });

    if (response.data?.success === false) {
      window.location = "/";
      return [];
    }

    return sortNovedades(getRows(response.data));
  };

  const saveNovedadForContract = async (contract, nota) => {
    const permission = await checkPerm("65", null, { requiredBit: "bitInsert" });
    if (!permission.allowed) {
      throw new Error("No tiene permisos para crear novedades.");
    }

    const relatedContracts = getContractsWithSamePlate(contracts, contract);
    const contractsToUpdate = [
      contract,
      ...relatedContracts.filter(
        (relatedContract) => String(relatedContract?.ContractID) !== String(contract?.ContractID)
      ),
    ];

    for (const relatedContract of contractsToUpdate) {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}/operaciones/setnovedadesporrecoger`,
        JSON.stringify({
          FKContractID: relatedContract.ContractID,
          NotasSeg: nota,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("tkn")}`,
          },
          withCredentials: true,
        }
      );

      if (response.data?.success === false) {
        throw new Error(response.data?.mensaje || "No fue posible guardar la novedad.");
      }
    }

    return requestNovedades(contract);
  };

  const loadContracts = async () => {
    if (view === "map") {
      markersRef.current.forEach(({ marker }) => detachMapMarker(marker));
      markersRef.current = [];
      infoWindowRef.current?.close();
      mapRef.current = null;
      setMapReady(false);
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/operaciones/gettraficoporrecoger`,
        { withCredentials: true }
      );

      if (response.data?.success === false) {
        window.location = "/";
        return;
      }

      const contratosPorRecoger = markRepeatedPlates(getRows(response.data));
      console.log("[TRAFICO_POR_RECOGER][LISTA_CONTRATOS]", {
        cantidad: contratosPorRecoger.length,
        contratos: contratosPorRecoger,
      });

      setContracts(contratosPorRecoger);
      setSelectedId(null);
    } catch (requestError) {
      console.error("Error al listar tráfico por recoger:", requestError);
      setError("No fue posible cargar los contratos por recoger.");
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const closeNovedadesModal = () => {
    setNovedadesModalOpen(false);
    loadContracts();
  };

  const makeDeviceAvailable = async (contract) => {
    const permission = await checkPerm("66");
    if (!permission.allowed) return;

    const deviceId = String(contract?.DeviceID ?? "").trim();
    if (!deviceId) {
      toast.error("El contrato no tiene un dispositivo asociado.");
      return;
    }

    const agenciaAsociada = Number.parseInt(contract?.FKAgencia ?? contract?.fkAgencia ?? contract?.fkagencia, 10);

    let agencias = [];
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}/operaciones2/getagencias`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("tkn")}`,
          },
          withCredentials: true,
        }
      );

      if (response.data?.success === false) {
        throw new Error(response.data?.mensaje || "No fue posible cargar las agencias.");
      }

      const agencyRows = response.data?.data?.recordset ?? response.data?.recordset ?? response.data?.data ?? [];
      agencias = Array.isArray(agencyRows) ? agencyRows : [];
    } catch (requestError) {
      console.error("Error al cargar agencias para disponibilizar el dispositivo:", requestError);
      toast.error("No fue posible cargar las agencias.");
      return;
    }

    const agencyOptions = agencias.reduce((options, agency) => {
      const agencyId = Number.parseInt(agency?.IDAgencia ?? agency?.IdAgencia ?? agency?.ID ?? agency?.FKAgencia, 10);
      if (!Number.isInteger(agencyId)) return options;

      const description = agency?.Descripcion ?? agency?.NombreAgencia ?? agency?.Agencia ?? `Agencia ${agencyId}`;
      options[String(agencyId)] = description;
      return options;
    }, {});

    if (Object.keys(agencyOptions).length === 0) {
      toast.error("No hay agencias disponibles para seleccionar.");
      return;
    }

    const defaultAgency = Number.isInteger(agenciaAsociada) && agencyOptions[String(agenciaAsociada)]
      ? String(agenciaAsociada)
      : "";

    const confirmation = await Swal.fire({
      title: "¿Cambiar dispositivo a disponible?",
      text: `El dispositivo ${deviceId} quedará disponible para ser asignado nuevamente. Selecciona la agencia de destino.`,
      icon: "question",
      input: "select",
      inputOptions: agencyOptions,
      inputValue: defaultAgency,
      inputPlaceholder: "Seleccione una agencia",
      inputAttributes: {
        "aria-label": "Agencia de destino",
      },
      preConfirm: (selectedAgency) => {
        if (!Number.isInteger(Number.parseInt(selectedAgency, 10))) {
          Swal.showValidationMessage("Debe seleccionar una agencia.");
          return false;
        }
        return selectedAgency;
      },
      showLoaderOnConfirm: true,
      showCancelButton: true,
      confirmButtonText: "Sí, disponibilizar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!confirmation.isConfirmed) return;

    const ultimaAgencia = Number.parseInt(confirmation.value, 10);
    if (!Number.isInteger(ultimaAgencia)) {
      toast.error("Debe seleccionar una agencia válida.");
      return;
    }

    Swal.fire({
      title: "Disponibilizando dispositivo...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      console.log("[TRAFICO_POR_RECOGER][DISPONIBILIZAR_DISPOSITIVO]", {
        DeviceID: deviceId,
        UltimaAgencia: ultimaAgencia,
      });

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}/operaciones/setdisponibledevice`,
        JSON.stringify({
          DeviceID: deviceId,
          UltimaAgencia: ultimaAgencia,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("tkn")}`,
          },
          withCredentials: true,
        }
      );

      if (response.data?.success === false) {
        throw new Error(response.data?.mensaje || "No fue posible disponibilizar el dispositivo.");
      }

      Swal.close();
      toast.success("Dispositivo disponibilizado correctamente.");
      await loadContracts();
    } catch (requestError) {
      console.error("Error al disponibilizar el dispositivo:", requestError);
      Swal.close();
      toast.error(requestError.message || "No fue posible disponibilizar el dispositivo.");
    }
  };

  useEffect(() => {
    loadContracts();
  }, []);

  useEffect(() => {
    document.body.classList.add("trafico-recoger-no-scroll");
    document.body.classList.add("trafico-responsive-page");

    return () => {
      document.body.classList.remove("trafico-recoger-no-scroll");
      document.body.classList.remove("trafico-responsive-page");
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenActive =
        document.fullscreenElement === pageContentRef.current ||
        document.webkitFullscreenElement === pageContentRef.current;
      setIsMapFullscreen(fullscreenActive);

      if (view === "map" && mapRef.current && window.google?.maps?.event) {
        window.setTimeout(() => {
          window.google.maps.event.trigger(mapRef.current, "resize");
        }, 150);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, [view]);

  const filteredContracts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const companyValues = new Set(selectedCompanies.map((option) => option.value));
    const agencyValues = new Set(selectedAgencies.map((option) => option.value));
    const delayValues = new Set(selectedDelayRanges.map((option) => option.value));

    const filtered = contracts.filter((contract) => {
      const matchesSearch = !term || [
        contract?.ContractID,
        contract?.DeviceID,
        contract?.NombreEmpresa,
        contract?.NombreConductor,
        contract?.PlacaTruck,
        contract?.Ref,
        contract?.Agencia,
      ].some((value) => displayValue(value).toLowerCase().includes(term));
      const matchesCompany = companyValues.size === 0 || companyValues.has(getFilterValue(contract, "NombreEmpresa"));
      const matchesAgency = agencyValues.size === 0 || agencyValues.has(getFilterValue(contract, "Agencia"));
      const delayCategory = getDelayCategory(contract, currentTime);
      const matchesDelay = delayValues.size === 0 || delayValues.has(delayCategory);

      return matchesSearch && matchesCompany && matchesAgency && matchesDelay;
    });

    if (!sortConfig.key) return filtered;

    return filtered
      .map((contract, index) => ({ contract, index, value: getSortValue(contract, sortConfig.key, currentTime) }))
      .sort((first, second) => {
        const firstValue = first.value;
        const secondValue = second.value;
        const firstEmpty = firstValue === null || firstValue === undefined || String(firstValue).trim() === "";
        const secondEmpty = secondValue === null || secondValue === undefined || String(secondValue).trim() === "";

        if (firstEmpty !== secondEmpty) return firstEmpty ? 1 : -1;
        if (firstEmpty && secondEmpty) return first.index - second.index;

        let result;
        if (typeof firstValue === "number" && typeof secondValue === "number") {
          result = firstValue - secondValue;
        } else {
          result = String(firstValue).localeCompare(String(secondValue), "es", {
            sensitivity: "base",
            numeric: true,
          });
        }

        return (sortConfig.direction === "desc" ? -result : result) || (first.index - second.index);
      })
      .map(({ contract }) => contract);
  }, [contracts, search, selectedCompanies, selectedAgencies, selectedDelayRanges, sortConfig, currentTime]);

  const handleSort = (key) => {
    setSortConfig((previous) => ({
      key,
      direction: previous.key === key && previous.direction === "asc" ? "desc" : "asc",
    }));
  };

  const companyOptions = useMemo(
    () => getCountedFilterOptions(contracts, "NombreEmpresa", "Sin empresa"),
    [contracts]
  );
  const agencyOptions = useMemo(
    () => getCountedFilterOptions(contracts, "Agencia", "Sin agencia"),
    [contracts]
  );
  const delayOptions = useMemo(() => {
    const counts = contracts.reduce((result, contract) => {
      const category = getDelayCategory(contract, currentTime);
      if (category) result[category] = (result[category] || 0) + 1;
      return result;
    }, {});

    return [
      { value: DELAY_RANGES.normal, label: "0 días", count: counts[DELAY_RANGES.normal] || 0, delayClass: DELAY_RANGES.normal },
      { value: DELAY_RANGES.yellow, label: "1 a 3 días", count: counts[DELAY_RANGES.yellow] || 0, delayClass: DELAY_RANGES.yellow },
      { value: DELAY_RANGES.orange, label: "4 a 8 días", count: counts[DELAY_RANGES.orange] || 0, delayClass: DELAY_RANGES.orange },
      { value: DELAY_RANGES.red, label: "9 días o más", count: counts[DELAY_RANGES.red] || 0, delayClass: DELAY_RANGES.red },
    ];
  }, [contracts, currentTime]);

  const focusContract = (contract) => {
    const coordinates = getCoordinates(contract);
    setSelectedId(contract?.ContractID);

    if (!coordinates || !mapRef.current) return;

    mapRef.current.panTo(coordinates);
    mapRef.current.setZoom(16);
    const marker = markersRef.current.find(
      (item) => String(item.contract?.ContractID) === String(contract?.ContractID)
    );
    marker?.marker?.setAnimation?.(window.google.maps.Animation.BOUNCE);
    window.setTimeout(() => marker?.marker?.setAnimation?.(null), 900);
    if (marker?.marker && window.google?.maps?.event) {
      window.google.maps.event.trigger(marker.marker, "click");
    }
  };

  const loadNovedades = async (contract) => {
    if (!contract?.ContractID) return;

    setNovedadesLoading(true);
    setNovedadesError("");

    try {
      setNovedades(await requestNovedades(contract));
    } catch (requestError) {
      console.error("Error al cargar novedades del contrato:", requestError);
      setNovedades([]);
      setNovedadesError("No fue posible cargar las novedades de este contrato.");
    } finally {
      setNovedadesLoading(false);
    }
  };

  useEffect(() => {
    if (!novedadesLoading && novedadesListRef.current) {
      novedadesListRef.current.scrollTop = novedadesListRef.current.scrollHeight;
    }
  }, [novedades, novedadesLoading]);

  const openNovedadesModal = async (contract) => {
    const permission = await checkPerm("65", null, { requiredBit: "bitInsert" });
    if (!permission.allowed) return;

    setSelectedContract(contract);
    setNovedadesModalOpen(true);
    setNovedades([]);
    setNotasSeg("");
    loadNovedades(contract);
  };

  const saveNovedad = async (event) => {
    event.preventDefault();
    const nota = notasSeg.trim();
    if (!selectedContract?.ContractID || !nota) return;

    setSavingNovedad(true);
    setNovedadesError("");

    try {
      setNovedades(await saveNovedadForContract(selectedContract, nota));
      setNotasSeg("");
    } catch (requestError) {
      console.error("Error al guardar novedad del contrato:", requestError);
      setNovedadesError("No fue posible guardar la novedad.");
    } finally {
      setSavingNovedad(false);
    }
  };

  const selectedPlateContracts = getContractsWithSamePlate(contracts, selectedContract);

  useEffect(() => {
    if (view !== "map") {
      markersRef.current.forEach(({ marker }) => detachMapMarker(marker));
      markersRef.current = [];
      infoWindowRef.current?.close();
      mapRef.current = null;
      setMapReady(false);
      return undefined;
    }

    if (mapRef.current || !mapContainerRef.current) return undefined;

    const initializeMap = () => {
      if (!window.google?.maps || !mapContainerRef.current || mapRef.current) return;

      mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
        center: DEFAULT_CENTER,
        zoom: 6,
        mapId: process.env.REACT_APP_GOOGLE_MAP_ID || "DEMO_MAP_ID",
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
      });
      registerOpenStreetMap(window.google, mapRef.current, { showControl: false });
      setMapReady(true);
    };

    initializeMap();
    if (!mapRef.current) {
      mapRetryRef.current = window.setInterval(initializeMap, 250);
    }

    return () => {
      if (mapRetryRef.current) window.clearInterval(mapRetryRef.current);
      mapRetryRef.current = null;
    };
  }, [view, loading]);

  useEffect(() => {
    const map = mapRef.current;
    if (view !== "map" || !mapReady || !map || !window.google?.maps) return;

    markersRef.current.forEach(({ marker }) => detachMapMarker(marker));
    markersRef.current = [];
    infoWindowRef.current?.close();

    const contractsWithCoordinates = filteredContracts
      .map((contract) => ({ contract, coordinates: getCoordinates(contract) }))
      .filter((item) => item.coordinates);

    if (contractsWithCoordinates.length === 0) {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(6);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    const infoWindow = new window.google.maps.InfoWindow();
    infoWindowRef.current = infoWindow;

    contractsWithCoordinates.forEach(({ contract, coordinates }) => {
      const delayCategory = getDelayCategory(contract, currentTime);
      const markerColor = DELAY_MARKER_COLORS[delayCategory] || DELAY_MARKER_COLORS.normal;
      const markerTitle = `${displayValue(contract.ContractID)} - ${displayValue(contract.DeviceID)}`;
      const markerApi = window.google.maps.marker;
      const marker = markerApi?.AdvancedMarkerElement && markerApi?.PinElement
        ? new markerApi.AdvancedMarkerElement({
            position: coordinates,
            map,
            title: markerTitle,
            content: new markerApi.PinElement({
              background: markerColor,
              borderColor: "#35627f",
              glyphColor: "#35627f",
            }).element,
          })
        : new window.google.maps.Marker({
            position: coordinates,
            map,
            title: markerTitle,
          });

      marker.addListener("click", async () => {
        const permission = await checkPerm("65", null, { requiredBit: "bitInsert" });
        if (!permission.allowed) return;

        setSelectedId(contract.ContractID);
        const requestId = infoNovedadesRequestRef.current + 1;
        infoNovedadesRequestRef.current = requestId;
        infoWindow.setContent(createMapInfoContent(contract, [], true));
        infoWindow.open({ map, anchor: marker });

        requestNovedades(contract)
          .then((novedadesData) => {
            if (infoNovedadesRequestRef.current !== requestId) return;
            infoWindow.setContent(createMapInfoContent(contract, novedadesData, false, saveNovedadForContract));
          })
          .catch((requestError) => {
            console.error("Error al cargar novedades del InfoWindow:", requestError);
            if (infoNovedadesRequestRef.current !== requestId) return;
            infoWindow.setContent(createMapInfoContent(contract, [], false, saveNovedadForContract));
          });
      });

      markersRef.current.push({ contract, marker });
      bounds.extend(coordinates);
    });

    map.fitBounds(bounds);
  }, [filteredContracts, view, mapReady]);

  useEffect(() => () => {
    if (mapRetryRef.current) window.clearInterval(mapRetryRef.current);
    markersRef.current.forEach(({ marker }) => detachMapMarker(marker));
  }, []);

  return (
    <div className={`layout-wrapper layout-content-navbar trafico-page trafico-recoger-page ${view === "map" ? "is-map-view" : ""}`}>
      <div className="layout-container">
        <div className="layout-page">
          <BarraTrafico onToggleNavigation={toggleNavigationMenu} />
          <div className="content-wrapper">
            <main ref={pageContentRef} className={`container-fluid flex-grow-1 container-p-y trafico-recoger-content ${isMapFullscreen ? "is-map-fullscreen" : ""}`}>
              <div className="trafico-recoger-heading">
                <div>
                  <h1>Equipos por recoger</h1>
                  <p>{filteredContracts.length} contrato(s) encontrados</p>
                </div>
                <div className="trafico-recoger-actions">
                  <div className="trafico-recoger-filter-select">
                    <Select
                      isMulti
                      isSearchable
                      isClearable
                      closeMenuOnSelect={false}
                      controlShouldRenderValue={false}
                      hideSelectedOptions={false}
                      options={companyOptions}
                      value={selectedCompanies}
                      onChange={(options) => setSelectedCompanies(options || [])}
                      placeholder={selectedCompanies.length ? (
                        <span className="trafico-recoger-filter-placeholder">
                          Empresas <span className="trafico-recoger-filter-count-badge">{selectedCompanies.length}</span>
                        </span>
                      ) : "Empresas..."}
                      formatOptionLabel={formatFilterOptionLabel}
                      noOptionsMessage={() => "No hay empresas disponibles"}
                      classNamePrefix="trafico-recoger-filter"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                      menuPosition="fixed"
                      styles={filterSelectStyles}
                      aria-label="Filtrar por empresas"
                    />
                  </div>
                  <div className="trafico-recoger-filter-select">
                    <Select
                      isMulti
                      isSearchable
                      isClearable
                      closeMenuOnSelect={false}
                      controlShouldRenderValue={false}
                      hideSelectedOptions={false}
                      options={agencyOptions}
                      value={selectedAgencies}
                      onChange={(options) => setSelectedAgencies(options || [])}
                      placeholder={selectedAgencies.length ? (
                        <span className="trafico-recoger-filter-placeholder">
                          Agencias <span className="trafico-recoger-filter-count-badge">{selectedAgencies.length}</span>
                        </span>
                      ) : "Agencias..."}
                      formatOptionLabel={formatFilterOptionLabel}
                      noOptionsMessage={() => "No hay agencias disponibles"}
                      classNamePrefix="trafico-recoger-filter"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                      menuPosition="fixed"
                      styles={filterSelectStyles}
                      aria-label="Filtrar por agencias"
                    />
                  </div>
                  <div className="trafico-recoger-filter-select">
                    <Select
                      isMulti
                      isSearchable={false}
                      isClearable
                      closeMenuOnSelect={false}
                      controlShouldRenderValue={false}
                      hideSelectedOptions={false}
                      options={delayOptions}
                      value={selectedDelayRanges}
                      onChange={(options) => setSelectedDelayRanges(options || [])}
                      placeholder={selectedDelayRanges.length ? (
                        <span className="trafico-recoger-filter-placeholder">
                          Retraso <span className="trafico-recoger-filter-count-badge">{selectedDelayRanges.length}</span>
                        </span>
                      ) : (
                        <span className="trafico-recoger-delay-summary" aria-label="Contadores de retraso">
                          <span>Retraso</span>
                          {delayOptions.map((option) => (
                            <span
                              key={option.value}
                              className={`trafico-recoger-filter-count-badge is-delay-${option.delayClass}`}
                              title={`${option.label}: ${option.count}`}
                            >
                              {option.count}
                            </span>
                          ))}
                        </span>
                      )}
                      formatOptionLabel={formatFilterOptionLabel}
                      components={{ Option: DelayOption }}
                      noOptionsMessage={() => "No hay retrasos disponibles"}
                      classNamePrefix="trafico-recoger-filter"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                      menuPosition="fixed"
                      styles={filterSelectStyles}
                      aria-label="Filtrar por retraso"
                    />
                  </div>
                  <label htmlFor="trafico-recoger-search">
                    <i className="bx bx-search" aria-hidden="true" />
                    <input id="trafico-recoger-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar contrato, dispositivo, empresa o referencia" />
                  </label>
                  <button type="button" className="trafico-recoger-refresh" onClick={loadContracts} disabled={loading}>
                    <i className="bx bx-refresh" aria-hidden="true" /> Actualizar
                  </button>
                  <div className="trafico-recoger-view-switch" role="group" aria-label="Cambiar vista">
                    <button type="button" className={view === "grid" ? "is-active" : ""} onClick={() => setView("grid")}>
                      <i className="bx bx-grid-alt" aria-hidden="true" /> Grilla
                    </button>
                    <button type="button" className={view === "map" ? "is-active" : ""} onClick={() => setView("map")}>
                      <i className="bx bx-map" aria-hidden="true" /> Mapa
                    </button>
                  </div>
                </div>
              </div>

              {loading && <div className="trafico-recoger-state"><PreloaderHistorico /></div>}
              {!loading && error && <div className="trafico-recoger-state trafico-recoger-state-error">{error}</div>}
              {!loading && !error && view === "grid" && (
                <div className="card trafico-recoger-table-card">
                  <div className="table-responsive text-nowrap trafico-recoger-table-scroll trafico-table-freeze-wrapper">
                    <table className="table table-hover trafico-recoger-table" id="tablaTRAF">
                      <thead className="FondoHeaderTabla2">
                        <tr>
                          <SortableHeader label="Empresa" sortKey={SORT_KEYS.company} sortConfig={sortConfig} onSort={handleSort} />
                          <SortableHeader label="Agencia" sortKey={SORT_KEYS.agency} sortConfig={sortConfig} onSort={handleSort} />
                          <SortableHeader label="Dispositivo" sortKey={SORT_KEYS.device} sortConfig={sortConfig} onSort={handleSort} />
                          <SortableHeader label="Conductor" sortKey={SORT_KEYS.driver} sortConfig={sortConfig} onSort={handleSort} />
                          <SortableHeader label="Última novedad" sortKey={SORT_KEYS.novelty} sortConfig={sortConfig} onSort={handleSort} />
                          <SortableHeader label="Últ. reporte" sortKey={SORT_KEYS.lastReport} sortConfig={sortConfig} onSort={handleSort} />
                          <SortableHeader label="Tiem. desinst." sortKey={SORT_KEYS.uninstallTime} sortConfig={sortConfig} onSort={handleSort} />
                          <SortableHeader label="Ubicación" sortKey={SORT_KEYS.location} sortConfig={sortConfig} onSort={handleSort} />
                          <th aria-label="Acciones" />
                        </tr>
                      </thead>
                      <tbody>
                        {filteredContracts.map((contract, index) => {
                          const key = `${contract?.ContractID ?? "sin-id"}-${index}`;
                          const delayCategory = getDelayCategory(contract, currentTime);
                          return (
                            <tr
                              key={key}
                              className={`${String(selectedId) === String(contract?.ContractID) ? "is-selected" : ""} ${delayCategory ? `is-delay-${delayCategory}` : ""}`}
                            >
                              <td data-label="Empresa"><strong>{displayValue(contract?.NombreEmpresa)}</strong></td>
                              <td data-label="Agencia">{displayValue(contract?.Agencia)}</td>
                              <td data-label="Dispositivo">{displayValue(contract?.DeviceID)}</td>
                              <td data-label="Conductor">
                                <strong>PLACA:</strong> <PlateLabel contract={contract} />
                                <br />
                                <strong>NOMBRE:</strong> {displayValue(contract?.NombreConductor)}
                                <br />
                                <strong>TEL:</strong> {displayValue(contract?.MovilConductor)}
                              </td>
                              <td data-label="Última novedad">
                                <button
                                  type="button"
                                  className="trafico-recoger-novelty-trigger"
                                  onClick={() => openNovedadesModal(contract)}
                                  aria-label={`Abrir novedades de ${displayValue(contract?.ContractID)}`}
                                >
                                  {displayValue(contract?.LastNovedadRecogida?.trim?.() || contract?.LastReportNota)}
                                </button>
                              </td>
                              <td data-label="Últ. reporte">{formatElapsedReportTime(contract?.LoksysServerTime, currentTime)}</td>
                              <td data-label="Tiem. desinstalación">{formatElapsedReportTime(contract?.FechaHoraFin, currentTime)}</td>
                              <td data-label="Ubicación">{displayValue(contract?.LastReportUbica)}</td>
                              <td data-label="Acciones" className="trafico-recoger-actions-cell">
                                <div className="trafico-recoger-actions-row">
                                  <Tooltip content="Disponibilizar dispositivo" placement="left">
                                    <button
                                      type="button"
                                      className="trafico-recoger-copy-button trafico-recoger-available-button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        makeDeviceAvailable(contract);
                                      }}
                                      aria-label="Disponibilizar dispositivo"
                                    >
                                      <i className="bx bx-check-circle" aria-hidden="true" />
                                    </button>
                                  </Tooltip>
                                  <Tooltip content="Copiar datos del contrato" placement="left">
                                    <button
                                      type="button"
                                      className="trafico-recoger-copy-button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        copyContractData(contract);
                                      }}
                                      aria-label="Copiar datos del contrato"
                                    >
                                      <i className="bx bx-copy" aria-hidden="true" />
                                    </button>
                                  </Tooltip>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {filteredContracts.length === 0 && <tr><td colSpan="9" className="trafico-recoger-empty">No se encontraron contratos.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!loading && !error && view === "grid" && (
                <div className="trafico-recoger-mobile-list" aria-label="Contratos por recoger">
                  {filteredContracts.map((contract, index) => {
                    const key = `${contract?.ContractID ?? "sin-id"}-mobile-${index}`;
                    const novelty = contract?.LastNovedadRecogida?.trim?.() || contract?.LastReportNota;
                    const delayCategory = getDelayCategory(contract, currentTime);

                    return (
                      <article
                        key={key}
                        className={`trafico-recoger-mobile-card ${String(selectedId) === String(contract?.ContractID) ? "is-selected" : ""} ${delayCategory ? `is-delay-${delayCategory}` : ""}`}
                      >
                        <div className="trafico-recoger-mobile-card-header">
                          <div>
                            <strong>{displayValue(contract?.NombreEmpresa)}</strong>
                            <span>{displayValue(contract?.Agencia)}</span>
                          </div>
                          <div className="trafico-recoger-mobile-card-actions">
                            <Tooltip content="Disponibilizar dispositivo" placement="bottom">
                              <button
                                type="button"
                                className="trafico-recoger-copy-button trafico-recoger-available-button"
                                aria-label="Disponibilizar dispositivo"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  makeDeviceAvailable(contract);
                                }}
                              >
                                <i className="bx bx-check-circle" aria-hidden="true" />
                              </button>
                            </Tooltip>
                            <Tooltip content="Copiar datos del contrato" placement="bottom">
                              <button
                                type="button"
                                className="trafico-recoger-copy-button"
                                aria-label="Copiar datos del contrato"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  copyContractData(contract);
                                }}
                              >
                                <i className="bx bx-copy" aria-hidden="true" />
                              </button>
                            </Tooltip>
                          </div>
                        </div>

                        <div className="trafico-recoger-mobile-card-device">
                          <i className="bx bx-mobile-alt" aria-hidden="true" />
                          <span><strong>Dispositivo:</strong> {displayValue(contract?.DeviceID)}</span>
                        </div>

                        <div className="trafico-recoger-mobile-card-grid">
                          <div>
                            <span className="trafico-recoger-mobile-label">Conductor</span>
                            <strong>PLACA: <PlateLabel contract={contract} /></strong>
                            <span>NOMBRE: {displayValue(contract?.NombreConductor)}</span>
                            <span>TEL: {displayValue(contract?.MovilConductor)}</span>
                          </div>
                          <div>
                            <span className="trafico-recoger-mobile-label">Ubicación</span>
                            <span>{displayValue(contract?.LastReportUbica)}</span>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="trafico-recoger-mobile-card-novelty"
                          onClick={() => openNovedadesModal(contract)}
                          aria-label={`Abrir novedades de ${displayValue(contract?.ContractID)}`}
                        >
                          <span className="trafico-recoger-mobile-label">Última novedad</span>
                          <span>{displayValue(novelty)}</span>
                        </button>

                        <div className="trafico-recoger-mobile-card-footer">
                          <div className="trafico-recoger-mobile-time-metric">
                            <i className="bx bx-time-five" aria-hidden="true" />
                            <div>
                              <span className="trafico-recoger-mobile-time-label">Últ. reporte</span>
                              <strong>{formatElapsedReportTime(contract?.LoksysServerTime, currentTime)}</strong>
                            </div>
                          </div>
                          <div className="trafico-recoger-mobile-time-metric">
                            <i className="bx bx-time-five" aria-hidden="true" />
                            <div>
                              <span className="trafico-recoger-mobile-time-label">Tiem. desinst.</span>
                              <strong>{formatElapsedReportTime(contract?.FechaHoraFin, currentTime)}</strong>
                            </div>
                          </div>
                          <i className="bx bx-chevron-right" aria-hidden="true" />
                        </div>
                      </article>
                    );
                  })}
                  {filteredContracts.length === 0 && (
                    <p className="trafico-recoger-empty">No se encontraron contratos.</p>
                  )}
                </div>
              )}

              {!loading && !error && view === "map" && (
                <section className="card trafico-recoger-map-card" aria-label="Mapa de contratos por recoger">
                  <div className="trafico-recoger-map-layout">
                    <div ref={mapContainerRef} className="trafico-recoger-map" />
                    <button
                      type="button"
                      className="trafico-recoger-map-fullscreen-button"
                      onClick={toggleMapFullscreen}
                      aria-label={isMapFullscreen ? "Salir de pantalla completa" : "Ver mapa en pantalla completa"}
                      title={isMapFullscreen ? "Salir de pantalla completa" : "Ver mapa en pantalla completa"}
                    >
                      <i className={`bx ${isMapFullscreen ? "bx-exit-fullscreen" : "bx-fullscreen"}`} aria-hidden="true" />
                    </button>
                    <div className="trafico-recoger-map-type-controls" role="group" aria-label="Tipo de mapa">
                      {[
                        ["roadmap", "Mapa"],
                        ["satellite", "Satélite"],
                        ["openstreetmap", "OpenStreetMap"],
                      ].map(([mapType, label]) => (
                        <button
                          key={mapType}
                          type="button"
                          className={selectedMapType === mapType ? "is-active" : ""}
                          onClick={() => handleMapTypeChange(mapType)}
                          aria-pressed={selectedMapType === mapType}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <aside className={`trafico-recoger-contract-list ${isContractListCollapsed ? "is-collapsed" : ""}`} aria-label="Lista de contratos por recoger">
                      <button
                        type="button"
                        className="trafico-recoger-contract-list-header"
                        onClick={() => setIsContractListCollapsed((collapsed) => !collapsed)}
                        aria-expanded={!isContractListCollapsed}
                        aria-label={isContractListCollapsed ? "Mostrar contratos" : "Ocultar contratos"}
                      >
                        <div className="trafico-recoger-contract-list-heading">
                          <strong>Contratos</strong>
                          <span>{filteredContracts.length}</span>
                        </div>
                        <span className="trafico-recoger-contract-list-toggle" aria-hidden="true">
                          <i className={`bx ${isContractListCollapsed ? "bx-chevron-right" : "bx-chevron-left"}`} aria-hidden="true" />
                        </span>
                      </button>
                      <div className="trafico-recoger-contract-list-scroll">
                        {filteredContracts.map((contract, index) => (
                          (() => {
                            const speed = displayValue(contract?.Speed ?? contract?.Velocidad ?? 0);
                            const location = contract?.LastReportUbica || contract?.Agencia || "Sin ubicación";
                            const lastTime = contract?.Tiempo || formatDate(contract?.LoksysServerTime);
                            const delayCategory = getDelayCategory(contract, currentTime);

                            return (
                              <div
                            key={`${contract?.ContractID ?? "sin-id"}-${index}`}
                            className={`trafico-recoger-contract-item ${String(selectedId) === String(contract?.ContractID) ? "is-selected" : ""} ${delayCategory ? `is-delay-${delayCategory}` : ""}`}
                            onClick={() => focusContract(contract)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") focusContract(contract);
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <div className="trafico-recoger-contract-item-heading">
                              <div className="trafico-recoger-contract-item-title">
                                <strong>{displayValue(contract?.NombreEmpresa)}</strong>
                                <span className="trafico-recoger-contract-item-plate"><i className="bx bx-credit-card" aria-hidden="true" /> <PlateLabel contract={contract} /></span>
                              </div>
                              <div className="trafico-recoger-contract-item-heading-actions">
                                <Tooltip content="Copiar datos del contrato" placement="bottom">
                                  <button
                                    type="button"
                                    className="trafico-recoger-contract-copy-button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      copyContractData(contract);
                                    }}
                                    aria-label="Copiar datos del contrato"
                                  >
                                    <i className="bx bx-copy" aria-hidden="true" />
                                  </button>
                                </Tooltip>
                                <Tooltip content="Disponibilizar dispositivo" placement="bottom">
                                  <button
                                    type="button"
                                    className="trafico-recoger-contract-copy-button trafico-recoger-available-button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      makeDeviceAvailable(contract);
                                    }}
                                    aria-label="Disponibilizar dispositivo"
                                  >
                                    <i className="bx bx-check-circle" aria-hidden="true" />
                                  </button>
                                </Tooltip>
                                <i className="bx bx-chevron-right trafico-recoger-contract-item-chevron" aria-hidden="true" />
                              </div>
                            </div>
                            <div className="trafico-recoger-contract-item-row">
                              <i className="bx bx-mobile-alt" aria-hidden="true" />
                              <span><b>Dispositivo:</b> {displayValue(contract?.DeviceID)}</span>
                            </div>
                            <div className="trafico-recoger-contract-item-row">
                              <i className="bx bx-map-pin" aria-hidden="true" />
                              <span><b>Ult. geocerca:</b> {displayValue(location)}</span>
                            </div>
                            <div className="trafico-recoger-contract-item-speed">
                              <i className="bx bx-tachometer" aria-hidden="true" /> {speed} km/h
                            </div>
                            <div className="trafico-recoger-contract-item-footer">
                              <div className="trafico-recoger-contract-item-icons" aria-label="Batería">
                                <i className="bx bx-battery" aria-hidden="true" />
                              </div>
                              <div className="trafico-recoger-contract-item-time-values">
                                <span className="trafico-recoger-contract-item-last-report">{lastTime}</span>
                                <span
                                  className={`trafico-recoger-uninstall-badge is-delay-${delayCategory || "normal"}`}
                                  title={`Tiempo de desinstalación: ${formatElapsedReportTime(contract?.FechaHoraFin, currentTime)}`}
                                >
                                  <i className="bx bx-time-five" aria-hidden="true" />
                                  <span>Tiem. desinst.</span>
                                  <strong>{formatElapsedReportTime(contract?.FechaHoraFin, currentTime)}</strong>
                                </span>
                              </div>
                            </div>
                          </div>
                            );
                          })()
                        ))}
                        {filteredContracts.length === 0 && <p className="trafico-recoger-contract-list-empty">No hay contratos para mostrar.</p>}
                      </div>
                    </aside>
                  </div>
                  <p className="trafico-recoger-map-summary">{filteredContracts.filter((contract) => getCoordinates(contract)).length} contrato(s) con coordenadas visibles en el mapa.</p>
                </section>
              )}
            </main>
          </div>
        </div>
      </div>
      <div className="layout-overlay layout-menu-toggle" />
      <Toaster position="top-right" toastOptions={{ duration: 2500 }} />
      <ModalBaseSm
        isOpen={novedadesModalOpen}
        onClose={closeNovedadesModal}
        title={`Novedades · ${displayValue(selectedContract?.ContractID)}`}
        className="trafico-recoger-novedades-modal"
      >
        <div className="trafico-recoger-novedades-content">
          {novedadesLoading && <p className="trafico-recoger-novedades-message">Cargando novedades...</p>}
          {!novedadesLoading && novedadesError && <p className="trafico-recoger-novedades-error">{novedadesError}</p>}
          {!novedadesLoading && !novedadesError && novedades.length === 0 && (
            <p className="trafico-recoger-novedades-message">Este contrato no tiene novedades registradas.</p>
          )}
          {savingNovedad && (
            <div className="trafico-recoger-novedades-sync-status" role="status" aria-live="polite">
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              Enviando novedad a {selectedPlateContracts.length} contrato(s) con la misma placa...
            </div>
          )}
          {!novedadesLoading && novedades.length > 0 && (
            <div ref={novedadesListRef} className="trafico-recoger-novedades-list">
              {novedades.map((novedad, index) => {
                const texto = novedad?.NotasSeg ?? novedad?.Nota ?? novedad?.Notas ?? novedad?.Descripcion ?? novedad?.Novedad ?? "";
                const fecha = novedad?.DatetimSeg ?? novedad?.DatetimeSeg ?? novedad?.Fecha ?? novedad?.FechaRegistro ?? novedad?.FechaCreacion ?? novedad?.createdAt;
                const usuario = novedad?.UserSeg ?? novedad?.Usuario ?? novedad?.NombreUsuario ?? "Usuario no identificado";
                return (
                  <article className="trafico-recoger-novedad" key={`${fecha ?? "novedad"}-${index}`}>
                    <div className="trafico-recoger-novedad-meta">
                      <strong>{displayValue(usuario)}</strong>
                      {fecha && <time>{formattedGlobalDate(fecha) || formatDate(fecha)}</time>}
                    </div>
                    <p>{displayValue(texto)}</p>
                  </article>
                );
              })}
            </div>
          )}
          <form onSubmit={saveNovedad} className="trafico-recoger-novedad-form">
            <label htmlFor="trafico-recoger-nota">Nueva novedad</label>
            <textarea
              id="trafico-recoger-nota"
              value={notasSeg}
              onChange={(event) => setNotasSeg(event.target.value)}
              placeholder="Escribe una novedad..."
              rows="3"
              maxLength="1000"
            />
            <div className="trafico-recoger-novedad-actions">
              <button
                type="button"
                className="trafico-recoger-novedad-cancel"
                onClick={closeNovedadesModal}
                disabled={savingNovedad}
              >
                Cancelar
              </button>
              <button type="submit" disabled={savingNovedad || !notasSeg.trim()}>
                {savingNovedad ? "Guardando..." : "Guardar novedad"}
              </button>
            </div>
          </form>
        </div>
      </ModalBaseSm>
    </div>
  );
};

export default TraficoPorRecoger;
